import { isOnlineOnlyRoute } from '../../shared/offline-policy.ts';
import { pathSchema, type PathEntry } from '../../shared/schemas/common.ts';
import { OFFLINE_META_KEY, OFFLINE_STORES } from './constants.ts';
import {
  deleteOfflineDb,
  EMPTY_META,
  deleteOtherOfflineDbs,
  type KeptProperty,
  type OfflineMeta,
  offlineStorageAvailable,
  openOfflineDb,
  type QueuedWrite,
  readAll,
  readOne,
  removeOne,
  type StoredBlob,
  type StoredEntity,
  withStore,
  writeOne,
} from './db.ts';

/** A create cannot be queued safely without the real ancestry its detail page needs. */
export class OfflineParentError extends Error {
  override readonly name = 'OfflineParentError';
}

// The one connection to the offline copy, and every operation the rest of
// the app performs on it.
//
// Which account's copy is open is decided by `useOfflineCopy` in
// session.ts; everything here works against whatever is open and answers
// "nothing kept" when none is (a signed-out app, a private window with no
// IndexedDB, a browser that refused the database). Never throwing for a
// missing copy is deliberate: the offline copy is a convenience over the
// network, and a browser that will not provide one must still run the app.

let open: { readonly userId: string; readonly db: IDBDatabase } | null = null;

/** The account whose copy is open, or null. */
export function openFor(): string | null {
  return open?.userId ?? null;
}

/**
 * Open the copy belonging to `userId`, replacing any other account's. A
 * different account signing in on this device deletes the copy that was
 * there before opening its own, so one person's sites never outlive their
 * session on a shared phone.
 */
export async function openCopyFor(userId: string): Promise<void> {
  if (open?.userId === userId) {
    return;
  }
  const previous = open;
  open = null;
  if (previous !== null) {
    previous.db.close();
    await deleteOfflineDb(previous.userId);
  }
  if (!offlineStorageAvailable()) {
    return;
  }
  // Whatever another account left here, whether or not this page saw it go.
  await deleteOtherOfflineDbs(userId);
  const db = await openOfflineDb(userId);
  try {
    // Older versions kept every successful GET, including secret reveals.
    // Remove those rows before exposing the copy; preserve map edits and blobs.
    await withStore(db, OFFLINE_STORES.entities, 'readwrite', async (store) => {
      const entities = await readAll<StoredEntity>(store);
      for (const entity of entities) {
        if (isOnlineOnlyRoute(`/api/${entity.key}`)) {
          await removeOne(store, entity.key);
        }
      }
    });
    open = { userId, db };
  } catch (error) {
    db.close();
    throw error;
  }
}

/**
 * Close and delete the open copy. Called on sign-out and on the 401 that
 * says the session is gone, so a device that was signed out — or signed out
 * everywhere from another device — keeps nothing on its next contact.
 */
export async function dropCopy(): Promise<void> {
  const previous = open;
  open = null;
  if (previous === null) {
    return;
  }
  previous.db.close();
  await deleteOfflineDb(previous.userId);
}

/** The open database, or null when there is no copy to work with. */
function database(): IDBDatabase | null {
  return open?.db ?? null;
}

// --- Entities --------------------------------------------------------------

/** Keep a read's reply, with the row version a later write will quote. */
export async function putEntity(entity: StoredEntity): Promise<void> {
  if (isOnlineOnlyRoute(`/api/${entity.key}`)) {
    throw new Error('This route cannot be kept offline');
  }
  const db = database();
  if (db === null) {
    return;
  }
  await withStore(db, OFFLINE_STORES.entities, 'readwrite', async (store) => {
    await writeOne(store, entity);
  });
}

/** A kept read, or null when this one was never kept. */
export async function getEntity(key: string): Promise<StoredEntity | null> {
  if (isOnlineOnlyRoute(`/api/${key}`)) {
    return null;
  }
  const db = database();
  if (db === null) {
    return null;
  }
  return await withStore(
    db,
    OFFLINE_STORES.entities,
    'readonly',
    async (store) => await readOne<StoredEntity>(store, key),
  );
}

/** Every kept read, for the Offline page's sizes and the refresh. */
export async function allEntities(): Promise<StoredEntity[]> {
  const db = database();
  if (db === null) {
    return [];
  }
  return await withStore(
    db,
    OFFLINE_STORES.entities,
    'readonly',
    async (store) => await readAll<StoredEntity>(store),
  );
}

/** A queued name or ID edit must also update every cached descendant breadcrumb. */
export async function updateKeptPaths(entry: PathEntry): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  const kept = await allEntities();
  await withStore(db, OFFLINE_STORES.entities, 'readwrite', async (store) => {
    for (const entity of kept) {
      const body = entity.body;
      if (typeof body !== 'object' || body === null || !('path' in body)) {
        continue;
      }
      const path = pathSchema.safeParse(body.path);
      if (
        !path.success ||
        !path.data.some((part) => part.type === entry.type && part.id === entry.id)
      ) {
        continue;
      }
      const changed = path.data.map((part) =>
        part.type === entry.type && part.id === entry.id
          ? {
              ...part,
              name: entry.name,
              ...(entry.identifier === undefined ? {} : { identifier: entry.identifier }),
            }
          : part,
      );
      await writeOne(store, { ...entity, body: { ...body, path: changed } });
    }
  });
}

/** Forget one kept read (a node that was deleted, or a property being forgotten). */
export async function dropEntity(key: string): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  await withStore(db, OFFLINE_STORES.entities, 'readwrite', async (store) => {
    await removeOne(store, key);
  });
}

/** Forget every kept read whose key starts with `prefix`. */
export async function dropEntitiesUnder(prefix: string): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  const kept = await allEntities();
  const keys = kept
    .map((entity) => entity.key)
    .filter((key) => key === prefix || key.startsWith(`${prefix}/`));
  await withStore(db, OFFLINE_STORES.entities, 'readwrite', async (store) => {
    for (const key of keys) {
      await removeOne(store, key);
    }
  });
}

// --- Queue -----------------------------------------------------------------

/** Put a write at the back of the queue; answers the number it was given. */
export async function enqueue(write: QueuedWrite): Promise<number> {
  const db = database();
  if (db === null) {
    throw new Error('There is no offline copy on this device to queue the change in');
  }
  return await withStore(db, OFFLINE_STORES.queue, 'readwrite', async (store) => {
    const key = await writeOne(store, write);
    return Number(key);
  });
}

/** Every queued write, oldest first. */
export async function listQueue(): Promise<QueuedWrite[]> {
  const db = database();
  if (db === null) {
    return [];
  }
  return await withStore(
    db,
    OFFLINE_STORES.queue,
    'readonly',
    async (store) => await readAll<QueuedWrite>(store),
  );
}

/** Replace a queued write with a changed copy of itself. */
export async function updateQueued(write: QueuedWrite): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  await withStore(db, OFFLINE_STORES.queue, 'readwrite', async (store) => {
    await writeOne(store, write);
  });
}

/** Take a write out of the queue: it landed, or it was discarded. */
export async function dequeue(id: number): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  await withStore(db, OFFLINE_STORES.queue, 'readwrite', async (store) => {
    await removeOne(store, id);
  });
}

// --- Blobs -----------------------------------------------------------------

/** Keep a photo thumbnail or a file for a property. */
export async function putBlob(blob: StoredBlob): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  await withStore(db, OFFLINE_STORES.blobs, 'readwrite', async (store) => {
    await writeOne(store, blob);
  });
}

export async function getBlob(key: string): Promise<StoredBlob | null> {
  const db = database();
  if (db === null) {
    return null;
  }
  return await withStore(
    db,
    OFFLINE_STORES.blobs,
    'readonly',
    async (store) => await readOne<StoredBlob>(store, key),
  );
}

/** Every blob kept for one property, so forgetting it can take them all. */
export async function blobsOf(propertyId: string): Promise<StoredBlob[]> {
  const db = database();
  if (db === null) {
    return [];
  }
  const all = await withStore(
    db,
    OFFLINE_STORES.blobs,
    'readonly',
    async (store) => await readAll<StoredBlob>(store),
  );
  return all.filter((blob) => blob.propertyId === propertyId);
}

/** Drop every blob kept for one property. */
export async function dropBlobsOf(propertyId: string): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  const held = await blobsOf(propertyId);
  const keys = held.map((blob) => blob.key);
  await withStore(db, OFFLINE_STORES.blobs, 'readwrite', async (store) => {
    for (const key of keys) {
      await removeOne(store, key);
    }
  });
}

// --- Meta ------------------------------------------------------------------

/** What the copy knows about itself; the empty state when there is none. */
export async function readMeta(): Promise<OfflineMeta> {
  const db = database();
  if (db === null) {
    return EMPTY_META;
  }
  const stored = await withStore(
    db,
    OFFLINE_STORES.meta,
    'readonly',
    async (store) => await readOne<OfflineMeta>(store, OFFLINE_META_KEY),
  );
  return stored ?? { ...EMPTY_META, key: OFFLINE_META_KEY };
}

/** Replace what the copy knows about itself. */
export async function writeMeta(meta: OfflineMeta): Promise<void> {
  const db = database();
  if (db === null) {
    return;
  }
  await withStore(db, OFFLINE_STORES.meta, 'readwrite', async (store) => {
    await writeOne(store, { ...meta, key: OFFLINE_META_KEY });
  });
}

/** Record (or replace) one kept property in the copy's own record of itself. */
export async function rememberKept(kept: KeptProperty): Promise<void> {
  const meta = await readMeta();
  await writeMeta({
    ...meta,
    kept: [kept, ...meta.kept.filter((entry) => entry.propertyId !== kept.propertyId)],
  });
}

/** Forget one kept property in the copy's own record of itself. */
export async function forgetKept(propertyId: string): Promise<void> {
  const meta = await readMeta();
  await writeMeta({
    ...meta,
    kept: meta.kept.filter((entry) => entry.propertyId !== propertyId),
  });
}
